import { Inbox, AlertTriangle, RefreshCw, SearchX } from "lucide-react";

// PATCH 35 — shared state + locale systems (M4/M5/M6/M14). One consistent set, adopted everywhere.

// M4 — deliberate empty state: icon, one sentence, a specific next action.
export function EmptyState({ icon: Icon = Inbox, title, action, onAction, testid = "empty-state" }) {
  return (
    <div data-testid={testid} style={{ textAlign: "center", padding: "40px 20px", color: "var(--muted)" }}>
      <Icon size={30} strokeWidth={1.5} aria-hidden="true" style={{ opacity: 0.7 }} />
      <p style={{ fontSize: 14, margin: "12px 0 14px", maxWidth: 380, marginLeft: "auto", marginRight: "auto" }}>{title}</p>
      {action && (
        <button className="k-btn k-btn-primary" data-testid={`${testid}-action`} style={{ fontSize: 13 }} onClick={onAction}>{action}</button>
      )}
    </div>
  );
}

// M5 — content-shaped shimmer skeletons, not a spinner.
export function Skeleton({ w = "100%", h = 14, r = 8, style }) {
  return <span className="k-skeleton" aria-hidden="true" style={{ display: "block", width: w, height: h, borderRadius: r, ...style }} />;
}
export function SkeletonList({ rows = 4, testid = "skeleton" }) {
  return (
    <div data-testid={testid} aria-busy="true" aria-live="polite" style={{ display: "grid", gap: 10 }}>
      {Array.from({ length: rows }).map((_, i) => (
        <div key={i} className="k-control" style={{ display: "grid", gap: 8 }}>
          <Skeleton w="55%" /><Skeleton w="90%" h={10} /><Skeleton w="35%" h={10} />
        </div>
      ))}
    </div>
  );
}

// M6 — specific, human error state with a real recovery action.
const HUMAN = {
  network: "We couldn't reach Kadana just now — this is usually a connection blip.",
  payment: "That payment didn't go through. No charge was made, and you can try again.",
  build: "The build didn't complete. Your credits weren't spent — you can re-run it.",
  connector: "This connector's endpoint isn't responding right now.",
  generic: "Something went wrong on our side.",
};
export function ErrorState({ kind = "generic", detail, onRetry, retryLabel = "Try again", testid = "error-state" }) {
  return (
    <div data-testid={testid} role="alert" style={{ textAlign: "center", padding: "34px 20px", color: "var(--muted)" }}>
      <AlertTriangle size={26} strokeWidth={1.6} aria-hidden="true" />
      <p style={{ fontSize: 14, margin: "10px 0 4px", color: "var(--heading)" }}>{HUMAN[kind] || HUMAN.generic}</p>
      {detail && <p style={{ fontSize: 12, marginBottom: 12 }}>{detail}</p>}
      {onRetry && (
        <button className="k-btn k-btn-ghost" data-testid={`${testid}-retry`} style={{ fontSize: 13 }} onClick={onRetry}>
          <RefreshCw size={13} /> {retryLabel}
        </button>
      )}
    </div>
  );
}

// M14 — locale-aware formatting. Uses the browser locale + tz by default, but a malformed
// navigator.language (e.g. "en-US@posix") must never crash a page — fall back to en-GB.
const LOCALE = (() => {
  const l = (typeof navigator !== "undefined" && navigator.language) || "en-GB";
  try { new Intl.NumberFormat(l); return l; } catch { return "en-GB"; }
})();
export const fmt = {
  num: (n) => (n == null ? "—" : new Intl.NumberFormat(LOCALE).format(n)),
  money: (minor, ccy = "GBP") => (minor == null ? "—" : new Intl.NumberFormat(LOCALE, { style: "currency", currency: ccy }).format(minor / 100)),
  date: (d) => (d ? new Intl.DateTimeFormat(LOCALE, { dateStyle: "medium" }).format(new Date(d)) : "—"),
  datetime: (d) => (d ? new Intl.DateTimeFormat(LOCALE, { dateStyle: "medium", timeStyle: "short" }).format(new Date(d)) : "—"),
  // PATCH 37 Mi5 — correct pluralisation, never "connector(s)". Prepends the formatted count.
  plural: (n, one, many) => `${fmt.num(n)} ${Number(n) === 1 ? one : (many || `${one}s`)}`,
  // PATCH 37 Mi15 — "2 hours ago" style; the exact timestamp is shown on hover by <RelTime>.
  rel: (d) => {
    if (!d) return "—";
    const then = new Date(d).getTime();
    if (Number.isNaN(then)) return "—";
    const s = Math.round((Date.now() - then) / 1000);
    const abs = Math.abs(s);
    const rtf = new Intl.RelativeTimeFormat(LOCALE, { numeric: "auto" });
    const units = [["year", 31536000], ["month", 2592000], ["week", 604800], ["day", 86400], ["hour", 3600], ["minute", 60], ["second", 1]];
    for (const [unit, secs] of units) {
      if (abs >= secs || unit === "second") return rtf.format(Math.round(-s / secs), unit);
    }
    return "just now";
  },
};

// PATCH 37 Mi3 — a specific empty *search* state (echoes the query + a hint), distinct from M4.
export function SearchEmpty({ query, hint, testid = "search-empty" }) {
  return (
    <div data-testid={testid} style={{ textAlign: "center", padding: "34px 20px", color: "var(--muted)" }}>
      <SearchX size={28} strokeWidth={1.5} aria-hidden="true" style={{ opacity: 0.7 }} />
      <p style={{ fontSize: 14, margin: "12px 0 4px", color: "var(--heading)" }}>
        No matches{query ? <> for “<span className="k-heading">{query}</span>”</> : ""}.
      </p>
      <p style={{ fontSize: 12, maxWidth: 360, marginLeft: "auto", marginRight: "auto" }}>
        {hint || "Try fewer words, a different spelling, or clear a filter."}
      </p>
    </div>
  );
}
